import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Shield, ShieldOff, Loader2 } from 'lucide-react';

type Profile = {
  user_id: string;
  username: string | null;
  auth_provider: string | null;
  created_at: string;
};

export default function AdminUsers() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [adminIds, setAdminIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const [p, r] = await Promise.all([
      supabase.from('profiles').select('user_id, username, auth_provider, created_at').order('created_at', { ascending: false }),
      supabase.from('user_roles').select('user_id, role').eq('role', 'admin'),
    ]);
    setProfiles((p.data as Profile[]) || []);
    setAdminIds(new Set((r.data || []).map((x: any) => x.user_id)));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleAdmin = async (userId: string, makeAdmin: boolean) => {
    setBusyId(userId);
    if (makeAdmin) {
      const { error } = await supabase.from('user_roles').insert({ user_id: userId, role: 'admin' });
      if (error) toast({ title: 'Failed', description: error.message, variant: 'destructive' });
      else toast({ title: 'Promoted to admin' });
    } else {
      const { error } = await supabase.from('user_roles').delete().eq('user_id', userId).eq('role', 'admin');
      if (error) toast({ title: 'Failed', description: error.message, variant: 'destructive' });
      else toast({ title: 'Admin revoked' });
    }
    setBusyId(null);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Users ({profiles.length})</h2>
      </div>
      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3 font-medium">Email / Username</th>
              <th className="p-3 font-medium">Provider</th>
              <th className="p-3 font-medium">Joined</th>
              <th className="p-3 font-medium">Role</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</td></tr>
            ) : profiles.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No users yet</td></tr>
            ) : profiles.map((p) => {
              const isAdmin = adminIds.has(p.user_id);
              const isSelf = p.user_id === user?.id;
              return (
                <tr key={p.user_id} className="border-t">
                  <td className="p-3 font-medium">{p.username || '—'}{isSelf && <span className="text-xs text-muted-foreground ml-2">(you)</span>}</td>
                  <td className="p-3"><Badge variant="outline">{p.auth_provider || 'email'}</Badge></td>
                  <td className="p-3 text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="p-3">{isAdmin ? <Badge>Admin</Badge> : <Badge variant="secondary">User</Badge>}</td>
                  <td className="p-3 text-right">
                    <Button
                      size="sm"
                      variant={isAdmin ? 'outline' : 'default'}
                      disabled={busyId === p.user_id || isSelf}
                      onClick={() => toggleAdmin(p.user_id, !isAdmin)}
                    >
                      {busyId === p.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isAdmin ? <><ShieldOff className="h-3.5 w-3.5 mr-1" />Revoke</> : <><Shield className="h-3.5 w-3.5 mr-1" />Promote</>}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}